/**
 * Step 7 -- Actuals (f14.3, design D6), rendered inside f10.3's step frame
 * through `STEP_BODIES`. Follows `cycle-step.html`:
 *
 *   upload -> process -> what did not match -> decide (commit / discard, then
 *   the variances in the queue) -> resulting figures -> accept
 *
 * One feed per reporting currency (USD and EUR today); the currency and the
 * month are read from the export, never from the filename. Accept is the
 * frame's own button and is offered only when the server says `can_accept`.
 */

import { useEffect, useRef, useState } from 'react';
import { ArrowRight, UploadCloud, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { ActualsStepBatch, CycleWorkflowStep } from '@/api/client';
import { isJobTerminal, useCycle } from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';
import { formatWhen } from '@/routes/data-import/importView';

import { formatAmount } from '../../reconciliation/reconciliationFormat';
import { refusalMessage } from '../usEngagements/usEngagementsView';
import { ActualsBatchReview } from './ActualsBatchReview';
import {
  batchStateLabel,
  batchStatePill,
  defaultBatch,
  feedBadge,
  periodLabel,
  queueLink,
  resultingUnits,
} from './actualsStepView';
import {
  useActualsReview,
  useActualsStepBatches,
  useActualsStepJob,
  useBatchPendingTotals,
  useBatchVarianceSummary,
  useRefreshActualsStep,
  useUploadActualsStepBatch,
} from './useActualsStep';

interface WatchedJob {
  id: string;
  kind: 'parse' | 'commit';
}

export function ActualsStep({ cycleId }: { cycleId: string; step: CycleWorkflowStep }) {
  const list = useActualsStepBatches(cycleId);
  const cycle = useCycle(cycleId);
  const upload = useUploadActualsStepBatch(cycleId);
  const [watched, setWatched] = useState<WatchedJob | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const job = useActualsStepJob(cycleId, watched?.id);

  useJobCompletion(cycleId, watched, job.data?.state);

  const batches = list.data?.batches ?? [];
  const selected = batches.find((batch) => batch.id === selectedId) ?? defaultBatch(batches);
  const parsing = selected ? ['RECEIVED', 'PARSING'].includes(selected.state.code) : false;
  const review = useActualsReview(cycleId, selected && !parsing ? selected.id : undefined);

  const startUpload = (file: File) =>
    upload.mutate(file, {
      onSuccess: (accepted) => {
        setSelectedId(accepted.batch_id);
        setWatched({ id: accepted.job_id, kind: 'parse' });
      },
    });

  if (list.isPending) return <p className="hint">Loading step 7…</p>;
  if (list.isError) return <ErrorAlert message={refusalMessage(list.error) ?? 'Step 7 could not be loaded.'} />;

  const current = batches.filter((batch) => batch.is_current);
  const cycleCode = cycle.data?.code ?? '—';

  return (
    <div className="stack">
      <ExpectedFeedsCard expected={list.data.expected} closingMonth={list.data.closing_month} />

      {upload.error ? <ErrorAlert message={refusalMessage(upload.error)} /> : null}
      {job.data && (!isJobTerminal(job.data.state) || job.data.state === 'FAILED') && (
        <div className="card">
          <div className="card-body">
            <JobProgress
              job={job.data}
              title={watched?.kind === 'commit' ? 'Storing the actuals and raising variances' : 'Reading the export'}
            />
          </div>
        </div>
      )}

      <ActualsDropzone onFile={startUpload} busy={upload.isPending} />

      {batches.length > 0 && (
        <BatchList batches={batches} selectedId={selected?.id ?? null} onSelect={setSelectedId} />
      )}

      {selected && parsing && (
        <p className="hint" role="status">
          Reading {selected.original_filename ?? 'the uploaded file'}…
        </p>
      )}
      {review.isError && <ErrorAlert message={refusalMessage(review.error)} />}
      {review.data && review.data.batch.state.code !== 'DISCARDED' && (
        <ActualsBatchReview
          cycleId={cycleId}
          cycleCode={cycleCode}
          review={review.data}
          onCommitStarted={(jobId) => setWatched({ id: jobId, kind: 'commit' })}
        />
      )}

      {batches
        .filter((batch) => batch.state.code === 'COMMITTED')
        .map((batch) => (
          <VariancesRaisedCard key={batch.id} cycleId={cycleId} batch={batch} />
        ))}

      {current.length > 0 && <ResultingFigures cycleId={cycleId} batches={current} />}
    </div>
  );
}

/** Refetch once a watched job reaches a terminal state -- once per job. */
function useJobCompletion(cycleId: string, watched: WatchedJob | null, state: string | undefined) {
  const refresh = useRefreshActualsStep(cycleId);
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!watched || !isJobTerminal(state) || handled.current === watched.id) return;
    handled.current = watched.id;
    refresh(watched.kind === 'commit' && state === 'SUCCEEDED');
  }, [watched, state, refresh]);
}

function ExpectedFeedsCard({
  expected,
  closingMonth,
}: {
  expected: { currency_code: string; business_units: { id: string; name: string }[];
    current_batch_id: string | null; committed_at: string | null }[];
  closingMonth: string;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Expected feeds</h3>
        <span className="hint">One DrHouse export per reporting currency · {periodLabel(closingMonth)}</span>
      </div>
      <div className="card-body">
        {expected.length === 0 ? (
          <p className="hint">No active Business Unit reports in any currency.</p>
        ) : (
          <table className="data compact">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Business Units</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {expected.map((feed) => {
                const badge = feedBadge(feed);
                return (
                  <tr key={feed.currency_code}>
                    <td className="mono">{feed.currency_code}</td>
                    <td>
                      {feed.business_units.length === 1
                        ? feed.business_units[0]?.name
                        : `${feed.business_units.length} units`}
                    </td>
                    <td>
                      <span className={badge.className}>{badge.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ActualsDropzone({ onFile, busy }: { onFile: (file: File) => void; busy: boolean }) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`dropzone${over ? ' is-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const file = event.dataTransfer.files.item(0);
        if (file) onFile(file);
      }}
    >
      <Wallet className="dz-icon" aria-hidden="true" />
      <div className="dz-title">Drop a DrHouse actuals export</div>
      <div className="hint">
        Upload the DrHouse export exactly as downloaded — the currency and month are read from the file.
      </div>
      <div className="btn-row dz-actions">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => input.current?.click()}>
          <UploadCloud aria-hidden="true" /> Choose file
        </button>
      </div>
      <input
        ref={input}
        type="file"
        className="sr-only"
        aria-label="Actuals export file"
        onChange={(event) => {
          const file = event.target.files?.item(0);
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
    </div>
  );
}

function BatchList({
  batches,
  selectedId,
  onSelect,
}: {
  batches: readonly ActualsStepBatch[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Batches</h3>
        <span className="hint">Newest first</span>
      </div>
      <div className="card-body">
        <table className="data compact">
          <thead>
            <tr>
              <th>Batch</th>
              <th>File</th>
              <th>Currency</th>
              <th>Month</th>
              <th>Uploaded</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => (
              <tr key={batch.id} aria-selected={batch.id === selectedId}>
                <td>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => onSelect(batch.id)}
                    aria-pressed={batch.id === selectedId}>
                    #{batch.batch_number}
                  </button>
                </td>
                <td className="truncate">{batch.original_filename ?? '—'}</td>
                <td className="mono">{batch.currency_code ?? '—'}</td>
                <td>{periodLabel(batch.period_month) ?? '—'}</td>
                <td>
                  {formatWhen(batch.uploaded_at)}
                  {batch.uploaded_by ? <span className="hint"> · {batch.uploaded_by.display_name}</span> : null}
                </td>
                <td>
                  <span className={batchStatePill(batch.state.code)}>{batchStateLabel(batch)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VariancesRaisedCard({ cycleId, batch }: { cycleId: string; batch: ActualsStepBatch }) {
  const summary = useBatchVarianceSummary(cycleId, batch.id);
  const totals = useBatchPendingTotals(cycleId, batch.id);
  const counts = summary.data?.counts_by_status;
  const pending = counts?.PENDING ?? batch.reconciliation?.PENDING ?? 0;
  return (
    <div className="card">
      <div className="card-head">
        <h3>
          Variances raised · {batch.currency_code ?? '—'} {periodLabel(batch.period_month) ?? ''}
        </h3>
        {pending > 0 ? (
          <span className="badge pill-warning">{pending} pending</span>
        ) : (
          <span className="badge pill-success">All decided</span>
        )}
      </div>
      <div className="card-body stack">
        {summary.isError ? (
          <p className="hint">{refusalMessage(summary.error)}</p>
        ) : (
          <div className="stat-grid">
            {(['PENDING', 'ACCEPTED', 'REJECTED', 'SUPERSEDED'] as const).map((code) => (
              <div className="stat" key={code}>
                <div className="stat-label">{code.charAt(0) + code.slice(1).toLowerCase()}</div>
                <div className="stat-value">{counts?.[code] ?? batch.reconciliation?.[code] ?? 0}</div>
              </div>
            ))}
          </div>
        )}
        {(totals.data?.pending_totals ?? []).length > 0 && (
          <p className="hint">
            Pending total:{' '}
            {(totals.data?.pending_totals ?? [])
              .map((total) => formatAmount(total.variance_amount, total.currency_code, { signed: true }))
              .join(' · ')}
          </p>
        )}
      </div>
      <div className="card-foot">
        <span className="hint">
          Batch #{batch.batch_number}
          {batch.is_current ? ' · current' : ' · superseded by a newer import'}
        </span>
        <Link className="btn btn-primary btn-sm" to={queueLink(cycleId, batch.id)}>
          Open reconciliation queue <ArrowRight aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

function ResultingFigures({ cycleId, batches }: { cycleId: string; batches: readonly ActualsStepBatch[] }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Resulting figures</h3>
        <span className="hint">Current committed actuals, per Business Unit · amounts in their own currency</span>
      </div>
      <div className="card-body">
        <div className="table-scroll">
          <table className="data compact">
            <thead>
              <tr>
                <th>Business Unit</th>
                <th>Currency</th>
                <th className="num">Rows</th>
                <th className="num">Month actual</th>
                <th className="num">Year to date</th>
                <th className="num">Pending variances</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <BatchUnitRows key={batch.id} cycleId={cycleId} batchId={batch.id} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** One current batch's units: f14.1 `figures.by_unit` and f14.2 `pending_by_unit`. */
function BatchUnitRows({ cycleId, batchId }: { cycleId: string; batchId: string }) {
  const review = useActualsReview(cycleId, batchId);
  const summary = useBatchVarianceSummary(cycleId, batchId);
  if (!review.data) {
    return (
      <tr>
        <td colSpan={6} className="hint">
          Loading figures…
        </td>
      </tr>
    );
  }
  const pending: Record<string, number> = {};
  for (const row of summary.data?.pending_by_unit ?? []) pending[row.business_unit.id] = row.pending;
  return (
    <>
      {resultingUnits([review.data]).map((unit) => (
        <tr key={unit.key}>
          <td>{unit.unit}</td>
          <td className="mono">{unit.currency}</td>
          <td className="num">{unit.rows}</td>
          <td className="num">{formatAmount(unit.month, unit.currency)}</td>
          <td className="num">{formatAmount(unit.ytd, unit.currency)}</td>
          <td className="num">{(unit.unitId && pending[unit.unitId]) || 0}</td>
        </tr>
      ))}
    </>
  );
}

function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <div>
        <div className="a-body">{message}</div>
      </div>
    </div>
  );
}

/**
 * Follows the frame's pending-variance blocker (design D6): one queue link per
 * current batch that still has pending variances.
 */
export function ActualsBlockerLinks({ cycleId }: { cycleId: string }) {
  const list = useActualsStepBatches(cycleId);
  const pending = (list.data?.batches ?? []).filter(
    (batch) => batch.is_current && (batch.reconciliation?.PENDING ?? 0) > 0,
  );
  if (!pending.length) return null;
  return (
    <div className="btn-row">
      {pending.map((batch) => (
        <Link key={batch.id} className="btn btn-secondary btn-sm" to={queueLink(cycleId, batch.id)}>
          {batch.currency_code ?? `Batch #${batch.batch_number}`} · {batch.reconciliation?.PENDING} pending{' '}
          <ArrowRight aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}
